import { Component, OnInit, Inject, ViewChild, HostListener, OnDestroy, signal } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators, NgForm, ValidatorFn, AbstractControl, ValidationErrors } from '@angular/forms';
import { ConfiguracionesTecnicasService } from '@servicios/configuracionesTecnicas.service';
import { DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { Subscription } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { tipoUnidad, estadoFormulario, matrixType, MatrixClassificationMap } from '@modelos/enum/enums';
import { compareObjects } from '@helpers/compareObjects';
import { getErrorMessage } from '@helpers/getErrorMessage';
import { GetTechnicalConfigQuery_techConfigs, TechConfigModels, classification, Branch } from '@interfaces/operation-result-types';
import { Apollo } from 'apollo-angular';
import { Get_BranchesQuery } from 'app/graphql/branch.graphql';
import { valueTakenValidatorGQL } from '@validators/valueTakenValidator_GQL';
import { NotificacionesService } from '@servicios/notificaciones.service';
import { showFormControl } from 'app/_sharedFuctions/shared-functions';
import { AuthenticationService } from '@servicios/authentication.service';
import { ConfigService } from '@servicios/config.service';
import { Router } from '@angular/router';
import { TechConfigLink } from '../table-tech-config/tech-config.interfaces';
import { AssayGroupService } from '@servicios/assay/assayGroup.service';
import { SampleControlTableService } from '@featuresModules/laboratory/samples/sampleControlTable/sampleControlTable.service';

@Component({
    selector: 'app-form-tech-config',
    templateUrl: './form-tech-config.component.html',
    styleUrls: ['./form-tech-config.component.css'],
    standalone: false
})
export class FormTechConfig implements OnInit, OnDestroy {

  /* VARIABLES */
  //Formulario
  form: UntypedFormGroup;
  title: string;
  modelName: string;
  private initialFormValue: { [key: string]: any };
  valueChangesForm$ = new Subscription();
  documentEditForm: NgForm
  showCodePatternHint = true
  currentModel: TechConfigModels
  showTranslation = signal([
    TechConfigModels.units,
    TechConfigModels.comments,
    TechConfigModels.cuantResults,
    TechConfigModels.methods,
    TechConfigModels.cualResults,
    TechConfigModels.matrix,
    TechConfigModels.containers,
    TechConfigModels.collectionType,
    TechConfigModels.matrix_type,
    TechConfigModels.matrix_subtype,
    TechConfigModels.examination_reasons])

  //Componente
  getErrorMessage = getErrorMessage;
  MatrixClassificationMap= MatrixClassificationMap
  guardadoDB = false;
  public tiposUnidad = [
    tipoUnidad.concentracion,
    tipoUnidad["concentracion m/m"],
    tipoUnidad["concentracion m/v"],
    tipoUnidad.masa,
    tipoUnidad.volumen
  ]

  public classification = Object.keys(classification)
  typeValue = matrixType
  isLoading = false;
  isSaving = false;
  spinnerMessage = 'Cargando...'
  textAreaValue= ['comments']
  
  assayGroupsList: any[] = [];
  sampleControlGroupsList: any[] = [];
  matricesList: any[] = [];
  matrixTypesList: any[] = [];
  
  sampleControlLabel = 'Grupos Peligros (Muestras Control)';
  showSampleControl = false;
  branches: Branch[] = [];
  nestedMode: boolean = false;
  /* VARIABLES */

  constructor(
    private fb: UntypedFormBuilder,
    public cts: ConfiguracionesTecnicasService,
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    private ns: NotificacionesService,
    private auth: AuthenticationService,
    private configService: ConfigService,
    public router: Router,
    private assayGroupService: AssayGroupService,
    private sampleControlTableService: SampleControlTableService,
    private apollo: Apollo
  ) { }

  @ViewChild('documentEditForm') set content(content: NgForm) {
    if (content) { // initially setter gets called with undefined
      this.documentEditForm = content;
    }
  }

  /*Escucha cuando el usuario presiona la tecla enter y hace next al Subject que controla que se hayan cumplido las validaciones asincronas y luego emite el evento emit del formulario */
  @HostListener('window:keyup', ['$event'])
  keyEvent(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      this.documentEditForm.valid && !this.guardadoDB ?
        this.documentEditForm.ngSubmit.emit() :
        null
    }
  }

  /* LIFE`S CYCLE HOOKS */
  ngOnInit() {
    this.isLoading = true;
    const data = this.config.data;
    this.currentModel = data.techConfigLink.model
    
    this.showSampleControl = this.configService.appSettings?.frontend?.modules?.laboratory?.sampleControl?.active ?? false;
    const altLabel = this.configService.appSettings?.frontend?.alternative_labels?.sampleControl?.find(l => l.lng === this.configService.language)?.value;
    if (altLabel) {
      this.sampleControlLabel = altLabel;
    }

    this.form = this.fb.group({
      code: [''],
      value: ['', [Validators.required]],
      kind: [data.techConfigLink.model == 'matrix' ? matrixType.low_moisture : null],
      factor: [null],
      abbreviation: [null],
      active: [true],
      classification: [],
      translation: [],
      criteria: [false],
      hold: [false],
      webActive: [true],
      revisionPeriod: [null],
      branchId: [null],
      assayGroupIds: [[]],
      sampleControlGroupIds: [[]],
      matrixIds: [[]],
      matrixTypeIds: [[]]
    })
    this.nestedMode = data.nestedMode || false;
    this.setCodePattern(data.techConfigLink.model)
    this.getTypeConfig(data.techConfigLink)

    //Subscripciones
    if (this.cts.modeForm == estadoFormulario.registroEdicion) {
      this.patchFormValues(data.data)
      this.guardadoDB = true;
      this.valueChangesForm$ = this.form.valueChanges.pipe(distinctUntilChanged()).subscribe(
        value => this.guardadoDB = compareObjects(value, this.initialFormValue, { ignoreEmptyValues: true })
      )
    } else {
      if (data?.techConfigLink?.model) {
        this.cts.getNextCode(data.techConfigLink.model).subscribe({
          next: ({ data: res }) => {
            if (res?.nextTechConfigCode && !this.form.controls.code.value) {
              this.form.controls.code.patchValue(res.nextTechConfigCode);
            }
          },
          error: (err) => console.error('Error fetching next code for techConfig', err)
        });
      }
    }
    this.initialFormValue = this.form.value;

    this.form.controls.code.setAsyncValidators([valueTakenValidatorGQL('code', this.initialFormValue, this.cts, 'techConfig', 'getOne', true, undefined, this.currentModel)])
    this.form.controls.value.setAsyncValidators([valueTakenValidatorGQL('value', this.initialFormValue, this.cts, 'techConfig', 'getOne', true, undefined, this.currentModel)])
    
    if (this.currentModel === 'matrix') {
      this.assayGroupService._getAll().subscribe(({ data }) => {
        this.assayGroupsList = data.assayGroups || [];
      });
      this.sampleControlTableService.getSampleControlGroups({ limit: 1000, page: 1 }).then(data => {
        this.sampleControlGroupsList = data.response || [];
      });
    }

    if (this.currentModel === 'sectors') {
      this.loadBranches();
    }

    if (this.currentModel === 'matrix_type') {
      this.cts.getAll('matrix').valueChanges.subscribe({
        next: ({ data }) => {
          this.matricesList = data?.techConfigs || [];
        }
      });
    }

    if (this.currentModel === 'matrix_subtype') {
      this.cts.getAll('matrix_type').valueChanges.subscribe({
        next: ({ data }) => {
          this.matrixTypesList = data?.techConfigs || [];
        }
      });
    }
    
    const initialBranchId = data.data?.branchId || data.data?.branch?.id;
    if (initialBranchId) {
      this.form.controls.branchId.patchValue(initialBranchId);
    }
    
    this.isLoading = false;

  }

  loadBranches() {
    this.apollo.query<any>({
      query: Get_BranchesQuery,
      fetchPolicy: 'network-only'
    }).subscribe({
      next: ({ data }) => {
        this.branches = data?.branches || [];
        // Si no tiene sucursal asignada en el formulario y hay sucursal principal o única, auto-asignarla
        if (!this.form.controls.branchId.value) {
          const defaultBranch = this.branches.find(b => b.is_main) || (this.branches.length === 1 ? this.branches[0] : null);
          if (defaultBranch) {
            this.form.controls.branchId.patchValue(defaultBranch.id);
          }
        }
      }
    });
  }

  ngOnDestroy() {
    this.valueChangesForm$.unsubscribe();
  }
  /* END LIFE`S CYCLE HOOKS */

  patchFormValues(data: GetTechnicalConfigQuery_techConfigs) {
    if (!data) return;
    this.form.controls.code.patchValue(data.code)
    this.form.controls.value.patchValue(data.value)
    this.form.controls.active.patchValue((data as any).active)
    this.form.controls.kind.patchValue(data.kind)
    this.form.controls.factor.patchValue(data.factor)
    this.form.controls.abbreviation.patchValue(data.abbreviation)
    this.form.controls.classification.patchValue(data.classification)
    this.form.controls.translation.patchValue(data.translation)
    this.form.controls.criteria.patchValue(data.criteria)
    this.form.controls.hold.patchValue(data.hold)
    this.form.controls.webActive.patchValue((data as any).webActive ?? true)
    this.form.controls.revisionPeriod.patchValue((data as any).revisionPeriod)
    const branchId = (data as any).branchId || (data as any).branch?.id;
    if (branchId) {
      this.form.controls.branchId.patchValue(branchId);
    }
    
    if (this.currentModel === 'matrix') {
      this.form.controls.assayGroupIds.patchValue(data.assaysG_v2?.map((g: any) => g.id) || []);
      this.form.controls.sampleControlGroupIds.patchValue(data.sample_control_group?.map((g: any) => g.id) || []);
    }

    if (this.currentModel === 'matrix_type') {
      this.form.controls.matrixIds.patchValue((data as any).matrices?.map((m: any) => m.id) || []);
    }

    if (this.currentModel === 'matrix_subtype') {
      this.form.controls.matrixTypeIds.patchValue((data as any).matrix_types?.map((mt: any) => mt.id) || []);
    }
  }

  /* CRUD FUNCTIONS */

  //Guardar el formulario.
  save() {
    this.spinnerMessage = 'Guardando...'
    this.isSaving = true
    let formData = this.form.value
    
    if (this.config.data.techConfigLink.model !== 'matrix') {
      delete formData.assayGroupIds;
      delete formData.sampleControlGroupIds;
    }

    if (this.config.data.techConfigLink.model !== 'matrix_type') {
      delete formData.matrixIds;
    }

    if (this.config.data.techConfigLink.model !== 'matrix_subtype') {
      delete formData.matrixTypeIds;
    }

    if (this.config.data.techConfigLink.model !== 'sectors') {
      delete formData.branchId;
    }

    if (this.config.data.techConfigLink.model !== 'doctors') {
      delete formData.webActive;
    }

    switch (this.config.data.techConfigLink.model) {
      case 'sectors':
        delete formData.factor
        delete formData.kind
        delete formData.classification
        delete formData.translation
        delete formData.hold
        delete formData.revisionPeriod
        break
      case 'docTypes':
        delete formData.factor
        delete formData.kind
        delete formData.classification
        delete formData.translation
        delete formData.criteria
        delete formData.hold
        break;
      case 'matrix':
        delete formData.factor
        delete formData.abbreviation
        delete formData.criteria
        delete formData.revisionPeriod
        break;
      case 'matrix_type':
        delete formData.factor
        delete formData.kind
        delete formData.abbreviation
        delete formData.classification
        delete formData.criteria
        delete formData.hold
        delete formData.revisionPeriod
        delete formData.matrixTypeIds
        break;
      case 'matrix_subtype':
        delete formData.factor
        delete formData.kind
        delete formData.abbreviation
        delete formData.classification
        delete formData.criteria
        delete formData.hold
        delete formData.revisionPeriod
        delete formData.matrixIds
        break;
      case 'units':
      case 'comments':
      case 'cuantResults':
      case 'cualResults':
      case 'methods':
      case 'examination_reasons':
      case 'collectionType':
      case 'containers':
        delete formData.factor
        delete formData.kind
        delete formData.abbreviation
        delete formData.classification
        delete formData.criteria
        delete formData.hold
        delete formData.revisionPeriod
      break;
      case 'doctors':
        delete formData.factor
        delete formData.kind
        delete formData.abbreviation
        delete formData.classification
        delete formData.translation
        delete formData.criteria
        delete formData.hold
        delete formData.revisionPeriod
        break;
        default:
          delete formData.factor
          delete formData.kind
          delete formData.abbreviation
          delete formData.classification
          delete formData.translation
          delete formData.criteria
          delete formData.hold
          delete formData.revisionPeriod
        break;
    }

    switch (this.cts.modeForm) {
      case estadoFormulario.registroNuevo:
        this.cts.save(formData, this.currentModel)
          .subscribe({
            next: () => {
              this.isSaving = false
              let data = this.cts.getAll(this.currentModel).refetch()
              this.ns.emitMessage(`${this.modelName} : Registro guardado`)
              this.ref.close(this.router.url != '/configuracionesTecnicas' ? data : 'saved')

            },
            error: err => {
              console.log(err)
              this.isSaving = false
              throw err
            }
          });
        break;
      case estadoFormulario.registroEdicion:
        this.cts.update(this.cts.idSelected, formData, this.currentModel)
          .subscribe(
            {
              next: () => {
                this.isSaving = false
                this.cts.getAll(this.currentModel).refetch()
                this.ns.emitMessage(`${this.modelName} : Registro editado`)
                this.ref.close('saved')
              },
              error: err => {
                console.log(err);
                this.isSaving = false
                throw err
              }
            }
          );;
        break;
    }
  }
  /* END CRUD FUNCTIONS */

  /* PERMISSIONS AND CONTROL VALUES */
  deshabilitarBotonGuardar() {
    return !this.form.valid || this.isSaving || this.form.pending || this.guardadoDB
  }

  /* END PERMISSIONS AND CONTROL VALUES */

  /*OTHER FUNCTIONS */

  getTypeConfig(link: TechConfigLink) {
    this.title = this.cts.modeForm == estadoFormulario.registroNuevo ? link.form.title.new : link.form.title.edit
    this.modelName = link.form.model.name
  }
  /* Función para que Mat-Select cargue el valor correspondiente cuando se edita un item */
  compareTipoObjects(object1: any, object2: any) {
    return object1 && object2 && object1.value == object2.value;
  }

  checkShowControl(form:string, control: string){
    return showFormControl(form, control, this.auth.tokenDecoded, this.configService.appSettings )
  }

  setCodePattern(model?: TechConfigModels){
    const isAlphanumeric = this.configService?.isCodeAlphanumeric
      ? this.configService.isCodeAlphanumeric(model)
      : !!(
          this.configService?.currentFrontSettings?.forms?.tech_config?.code?.alphanumeric &&
          this.configService?.currentFrontSettings?.forms?.tech_config?.code?.models?.includes(model)
        );

    if (isAlphanumeric) {
      this.form.get('code').setValidators(Validators.required)
      this.showCodePatternHint = false
    } else {
      const numericValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
        if (!control.value) return null;
        // In edit mode, if the code is untouched from its initial DB value, allow it even if historical alphanumeric
        const initialCode = this.initialFormValue?.code ?? this.config?.data?.data?.code;
        if (this.cts.modeForm === estadoFormulario.registroEdicion && control.value === initialCode) {
          return null;
        }
        return /^[0-9]+$/.test(control.value) ? null : { pattern: { requiredPattern: '^[0-9]+$', actualValue: control.value } };
      };

      this.form.get('code').setValidators([Validators.required, numericValidator])
      this.showCodePatternHint = true
    }
    this.form.get('code').updateValueAndValidity()
  }
  /*END OTHER FUNCTIONS */

  closeDialog() {
    this.ref.close(false);
  }
}
